// Settings as a tab, as the Mac's SettingsPane: a search and the pages on the left, the one picked on the right (one
// page per item, not one long scroll). Each page: its title, then its sections, each a title over a card of rows.
// Appearance and "On this computer" belong to this browser; the rest are saved to the account. Phone, sound, font
// size and cursor show up once the hub sends them (store Settings), already in this design.

import { IconBell, IconDeviceDesktop, IconInfoCircle, IconLink, IconMicrophone, IconMinus, IconMoon, IconPencil, IconPlus, IconSearch, IconSettings, IconSun, IconTrash, IconX } from '@tabler/icons-react';
import { useEffect, useRef, useState, useSyncExternalStore, type ReactNode } from 'react';
import { useStore, type LinksSetting, type Settings } from '../../store';
import { CursorGlyph, Ic, KeyCap, RadioMark, Segmented, Stepper, Switch, Tool, type TablerIcon } from '../ds';
import { setAppearance, useAppearance, type Appearance } from '../ds/theme';
import { notifyHere, onNotifyHere, setNotifyHere } from '../notify';
import { LinkArt } from '../LinkArt';
import { LINK_CHOICES, setLinksSetting } from '../openLinkStore';
import { updateSettings } from '../settingsStore';
import { isMac } from '../keys';
import { bindingLabel, caps, isModifierCode, MAX_BINDINGS, reservedBy, same, sameBinding, usable, type Binding, type Mod, type Shortcut, type VoiceBindings } from '../voiceGesture';
import { onVoiceShortcuts, resetVoiceBindings, setVoiceBindings, startRecordingKeys, voiceBindings, voiceBindingsChanged } from '../voiceKeys';

const Row = ({ label, sub, bad, children }: { label: string; sub: string; bad?: boolean; children: ReactNode }) => (
  <div className="ws-set-row">
    <div className="ws-set-text">
      <div className="ws-set-label">{label}</div>
      <div className="ws-set-sub" data-bad={bad ? '' : undefined}>
        {sub}
      </div>
    </div>
    {children}
  </div>
);

/** SettingsSection: its title (500 16/24 ink), 12 above its card of rows (r12, 1px line, 16 in); 32 above it. */
const Section = ({ id, title, card = true, children }: { id: string; title: string; card?: boolean; children: ReactNode }) => (
  <section id={`set-${id}`} className="ws-set-section" aria-labelledby={`set-${id}-h`}>
    <h2 id={`set-${id}-h`} className="ws-set-h">
      {title}
    </h2>
    {card ? <div className="ws-set-card-box">{children}</div> : children}
  </section>
);

function LinksChoice() {
  const value = useStore((s) => s.me?.settings.links ?? 'auto');
  const [saving, setSaving] = useState<LinksSetting | null>(null);
  const current = saving ?? value;
  return (
    <div role="radiogroup" aria-label="Open links" className="ws-set-cards">
      {LINK_CHOICES.map((c) => (
        <label key={c.value} className="ws-set-card" data-testid={`links-${c.value}`}>
          <span className="ws-set-card-art">
            <LinkArt to={c.value} />
          </span>
          <input
            type="radio"
            name="links"
            className="ds-sr"
            checked={current === c.value}
            disabled={saving !== null}
            onChange={async () => {
              setSaving(c.value);
              await setLinksSetting(c.value);
              setSaving(null);
            }}
          />
          <RadioMark on={current === c.value} />
          <span className="ws-set-text">
            <span className="ws-set-label">{c.label}</span>
            <span className="ws-set-sub">{c.hint}</span>
          </span>
        </label>
      ))}
    </div>
  );
}

type Action = 'push' | 'handsFree';
const ACTION_NAME: Record<Action, string> = { push: 'Push to talk', handsFree: 'Hands-free' };

/**
 * One way to start voice, as Wispr Flow's Shortcuts: its keys as caps, Change (listens for the next
 * keys) and Remove (when the action has more than one). While listening the field owns the keyboard (every key,
 * ⌘K and the voice keys included; only what the browser keeps for itself, like ⌘T, gets past). A chord commits on its key;
 * a modifier let go by itself commits as a hold (Push to talk) or a double tap (Hands-free). Esc or
 * leaving cancels. What's refused says why under it (a plain letter, one already taken, one cmx uses).
 */
function BindingField({
  action,
  value,
  listening,
  removable,
  onListen,
  onDone,
  onRemove,
}: {
  action: Action;
  value: Binding | null;
  listening: boolean;
  removable: boolean;
  onListen: () => void;
  onDone: (b: Binding | null) => string | null;
  onRemove: () => void;
}) {
  const mac = isMac();
  const [problem, setProblem] = useState('');
  const [mods, setMods] = useState<string[]>([]);
  const keysRef = useRef<HTMLButtonElement>(null);
  // the latest onDone, so a re-render above doesn't restart the listening
  const doneRef = useRef(onDone);
  doneRef.current = onDone;
  useEffect(() => {
    if (!listening) return;
    keysRef.current?.focus(); // so leaving it (a click elsewhere) cancels
    // While listening this field owns the keyboard: the app's shortcuts and the voice keys stand down (they listen on
    // the window too, and were added first, so they check this rather than rely on being stopped).
    const release = startRecordingKeys();
    setProblem('');
    setMods([]);
    let lone: string | null = null;
    const now = (e: KeyboardEvent) => (['Control', 'Alt', 'Shift', 'Meta'] as const).filter((m) => (m === 'Control' ? e.ctrlKey : m === 'Alt' ? e.altKey : m === 'Shift' ? e.shiftKey : e.metaKey));
    const commit = (b: Binding) => {
      const why = doneRef.current(b);
      setProblem(why ?? '');
      setMods([]);
    };
    // Every key is ours: the browser's default is stopped where it allows (⌘T, ⌘W, ⌘N, ⌘Q and the system's can't be),
    // and nothing else on the window or the page sees it.
    const take = (e: KeyboardEvent) => {
      e.preventDefault();
      e.stopPropagation();
      e.stopImmediatePropagation();
    };
    const down = (e: KeyboardEvent) => {
      take(e);
      if (e.repeat) return; // a key held down is one press
      if (e.key === 'Escape' && !e.ctrlKey && !e.altKey && !e.metaKey && !e.shiftKey) return void doneRef.current(null);
      if (isModifierCode(e.code)) {
        const held = now(e);
        setMods(held);
        lone = held.length === 1 ? e.key : null;
        return;
      }
      lone = null;
      const s: Shortcut = { code: e.code, alt: e.altKey, ctrl: e.ctrlKey, meta: e.metaKey, shift: e.shiftKey };
      if (!usable(s)) return setProblem(mac ? 'Add ⌃, ⌥ or ⌘ so typing still works.' : 'Add Ctrl, Alt or Meta so typing still works.');
      const reserved = reservedBy(s, mac);
      if (reserved) return setProblem(reserved);
      commit({ kind: 'chord', ...s });
    };
    const up = (e: KeyboardEvent) => {
      take(e);
      if (lone && e.key === lone) {
        const mod = lone as Mod;
        lone = null;
        if (action === 'push' && mod === 'Shift') return setProblem('Holding Shift by itself would get in the way of typing.');
        return commit({ kind: action === 'push' ? 'hold' : 'double', mod });
      }
      setMods(now(e));
    };
    window.addEventListener('keydown', down, true);
    window.addEventListener('keyup', up, true);
    return () => {
      window.removeEventListener('keydown', down, true);
      window.removeEventListener('keyup', up, true);
      release();
    };
  }, [listening, action, mac]);
  const name = ACTION_NAME[action];
  const shown = value ? caps(value, mac) : [];
  return (
    <div className="ws-vk-item">
      {/* listening, a press in the field keeps the keys' focus (so it keeps listening); × still cancels */}
      <div className="ws-vk-field" onMouseDown={(e) => listening && e.preventDefault()} data-listening={listening ? '' : undefined} data-bad={problem ? '' : undefined} data-testid={`voice-binding-${action}`}>
        <button
          ref={keysRef}
          type="button"
          className="ws-vk-keys"
          aria-pressed={listening}
          aria-label={listening ? `${name}: press the new shortcut, or Esc to cancel` : `${name}: ${value ? bindingLabel(value, mac) : 'none'}. Change`}
          data-listening={listening ? '' : undefined}
          data-testid={`voice-key-${action}`}
          onClick={() => !listening && onListen()}
          onBlur={() => listening && doneRef.current(null)}
        >
          {listening ? (
            mods.length ? (
              mods.map((m) => (
                <KeyCap key={m} cap={caps({ kind: 'hold', mod: m as Mod }, mac)[0]} />
              ))
            ) : (
              <span className="ws-vk-wait">Press keys…</span>
            )
          ) : (
            <>
              {value?.kind === 'double' && <span className="ws-vk-pre">Double tap</span>}
              {shown.map((c, i) => (
                <KeyCap key={i} cap={c} />
              ))}
            </>
          )}
        </button>
        {listening ? (
          <span className="ws-vk-tools">
            <Tool icon={IconX} iconSize={14} size="xs" radius={6} label="Cancel" sub="esc" onClick={() => onDone(null)} data-testid={`voice-cancel-${action}`} />
          </span>
        ) : (
          <span className="ws-vk-tools">
            <Tool icon={IconPencil} iconSize={14} size="xs" radius={6} label="Change" onClick={onListen} aria-label={`Change ${name} shortcut ${value ? bindingLabel(value, mac) : ''}`} data-testid={`voice-edit-${action}`} />
            {removable && <Tool icon={IconTrash} iconSize={14} size="xs" radius={6} label="Remove" onClick={onRemove} aria-label={`Remove ${name} shortcut ${value ? bindingLabel(value, mac) : ''}`} data-testid={`voice-remove-${action}`} />}
          </span>
        )}
      </div>
      {problem && (
        <p className="ws-vk-problem" role="alert">
          {problem}
        </p>
      )}
      <span className="ds-sr" role="status">
        {listening ? 'Listening for a shortcut' : ''}
      </span>
    </div>
  );
}

/** An action's ways to start, one to three, and + for another (Flow's Shortcuts card, in Settings' row). */
function BindingList({ action, all }: { action: Action; all: VoiceBindings }) {
  const list = all[action];
  const other: Action = action === 'push' ? 'handsFree' : 'push';
  /** Which one listens: an index, or `list.length` for one being added. */
  const [listening, setListening] = useState<number | null>(null);
  const adding = listening === list.length;
  const rows: (Binding | null)[] = adding ? [...list, null] : list;
  const done = (i: number) => (b: Binding | null): string | null => {
    if (!b) {
      setListening(null);
      return null;
    }
    const takenHere = list.some((x, j) => j !== i && sameBinding(x, b));
    if (takenHere) return `${ACTION_NAME[action]} already uses this.`;
    if (all[other].some((x) => sameBinding(x, b) || (x.kind === 'chord' && b.kind === 'chord' && same(x, b)))) return `${ACTION_NAME[other]} already uses this.`;
    const next = [...list];
    next[i] = b;
    setVoiceBindings(action, next);
    setListening(null);
    return null;
  };
  return (
    <div className="ws-vk-list" role="group" aria-label={`${ACTION_NAME[action]} shortcuts`}>
      {rows.map((b, i) => (
        <div key={i} className="ws-vk-line">
          <BindingField
            action={action}
            value={b}
            listening={listening === i}
            removable={list.length > 1 && i < list.length}
            onListen={() => setListening(i)}
            onDone={done(i)}
            onRemove={() => setVoiceBindings(action, list.filter((_, j) => j !== i))}
          />
          {i === rows.length - 1 ? (
            <Tool
              icon={IconPlus}
              iconSize={16}
              size="md"
              className="ws-vk-add"
              label={rows.length >= MAX_BINDINGS ? `Up to ${MAX_BINDINGS}` : 'Add a shortcut'}
              disabled={rows.length >= MAX_BINDINGS || adding}
              onClick={() => setListening(list.length)}
              aria-label={`Add a ${ACTION_NAME[action]} shortcut`}
              data-testid={`voice-add-${action}`}
            />
          ) : (
            <span className="ws-vk-add-room" aria-hidden="true" />
          )}
        </div>
      ))}
    </div>
  );
}

function VoiceKeys() {
  const all = useSyncExternalStore(onVoiceShortcuts, voiceBindings);
  const changed = useSyncExternalStore(onVoiceShortcuts, voiceBindingsChanged);
  const mac = isMac();
  return (
    <>
      <section id="set-voice-keys" className="ws-set-section" aria-labelledby="set-voice-keys-h">
        <div className="ws-set-hrow">
          <h2 id="set-voice-keys-h" className="ws-set-h">
            Shortcuts
          </h2>
          {changed && (
            <button type="button" className="ws-vk-reset" onClick={resetVoiceBindings} data-testid="voice-reset">
              Reset to defaults
            </button>
          )}
        </div>
        <div className="ws-set-card-box">
          <div className="ws-set-row ws-vk-row">
            <div className="ws-set-text">
              <div className="ws-set-label">Push to talk</div>
              <div className="ws-set-sub">Hold to talk, let go to insert</div>
            </div>
            <BindingList action="push" all={all} />
          </div>
          <div className="ws-set-row ws-vk-row">
            <div className="ws-set-text">
              <div className="ws-set-label">Hands-free</div>
              <div className="ws-set-sub">Press to start, again to insert</div>
            </div>
            <BindingList action="handsFree" all={all} />
          </div>
        </div>
        {/* what a browser can't hand a page, said before it's tried: the keys here aren't all the Mac app's */}
        <p className="ws-vk-note" data-testid="voice-keys-note">
          <Ic icon={IconInfoCircle} size={16} />
          <span>
            {mac
              ? 'In a browser, fn and the browser’s own shortcuts (⌘T, ⌘W, ⌘N, ⌘Q) can’t be used. Use ⌃, ⌥ or ⌘ on its own, or with a key. fn works in the Mac app.'
              : 'In a browser, the browser’s own shortcuts (Ctrl+T, Ctrl+W, Ctrl+N) can’t be used. Use Ctrl, Alt or Meta on its own, or with a key.'}
          </span>
        </p>
      </section>
    </>
  );
}

const save = (patch: Partial<Settings>) => void updateSettings(patch);

type Page = { id: string; title: string; icon: TablerIcon; terms: string };

export function SettingsView() {
  const appearance = useAppearance();
  const s = useStore((st) => st.me?.settings);
  const here = useSyncExternalStore(onNotifyHere, notifyHere);
  const [page, setPage] = useState('general');
  const [query, setQuery] = useState('');

  const terminal = s?.fontSize !== undefined || s?.cursor !== undefined;
  const pages: Page[] = [
    { id: 'general', title: 'General', icon: IconSettings, terms: 'appearance theme light dark system terminal font size cursor' },
    { id: 'links', title: 'Links', icon: IconLink, terms: 'open links automatically computer cloud machine browser desktop dev servers preview sign-ins' },
    { id: 'notify', title: 'Notifications', icon: IconBell, terms: 'notifications banner agent needs you phone sound' },
    { id: 'voice', title: 'Voice', icon: IconMicrophone, terms: 'voice dictation microphone shortcut keys push to talk hold hands-free fn key' },
  ];
  const q = query.trim().toLowerCase();
  const shown = q ? pages.filter((p) => p.title.toLowerCase().includes(q) || p.terms.includes(q)) : pages;
  const current = pages.find((p) => p.id === page) ?? pages[0];

  return (
    <div className="ws-view ws-settings">
      <nav className="ws-set-nav" aria-label="Settings">
        {/* SettingsSearch: typing narrows the pages to those with a matching setting; Return opens the first */}
        <label className="ws-set-search">
          <Ic icon={IconSearch} size={14} />
          <input
            type="search"
            placeholder="Search"
            aria-label="Search settings"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && shown[0]) setPage(shown[0].id);
              if (e.key === 'Escape' && query) (e.preventDefault(), setQuery(''));
            }}
            data-testid="settings-search"
          />
          {query && (
            <button type="button" className="ws-set-search-clear" aria-label="Clear the search" onClick={() => setQuery('')}>
              <Ic icon={IconX} size={12} />
            </button>
          )}
        </label>
        {shown.map((n) => (
          <button
            type="button"
            key={n.id}
            className="ws-set-link"
            aria-current={page === n.id ? 'page' : undefined}
            onClick={() => setPage(n.id)}
            data-testid={`settings-nav-${n.id}`}
          >
            <Ic icon={n.icon} size={16} />
            {n.title}
          </button>
        ))}
        {!shown.length && <p className="ws-set-none">No settings match.</p>}
      </nav>
      {/* a new page starts at its top */}
      <div className="ws-set-body" key={current.id}>
        <div className="ws-set-col">
          <h1 className="ws-set-title">{current.title}</h1>
          {current.id === 'general' && (
            <>
              <Section id="appearance" title="Appearance">
                <Row label="Theme" sub="Light, dark, or match your computer.">
                  <Segmented<Appearance>
                    label="Appearance"
                    value={appearance}
                    onChange={setAppearance}
                    items={[
                      { value: 'light', label: 'Light', icon: IconSun },
                      { value: 'dark', label: 'Dark', icon: IconMoon },
                      { value: 'system', label: 'System', icon: IconDeviceDesktop },
                    ]}
                  />
                </Row>
              </Section>
              {terminal && (
                <Section id="terminal" title="Terminal">
                  {s?.fontSize !== undefined && (
                    <Row label="Font size" sub="Text in every session.">
                      <Stepper
                        label="Font size"
                        value={`${s.fontSize} px`}
                        decIcon={IconMinus}
                        incIcon={IconPlus}
                        onDec={() => save({ fontSize: Math.max(10, s.fontSize! - 1) })}
                        onInc={() => save({ fontSize: Math.min(20, s.fontSize! + 1) })}
                      />
                    </Row>
                  )}
                  {s?.cursor !== undefined && (
                    <Row label="Cursor" sub="Shape of the cursor in the terminal.">
                      <Segmented<NonNullable<Settings['cursor']>>
                        label="Cursor"
                        value={s.cursor}
                        onChange={(cursor) => save({ cursor })}
                        items={[
                          { value: 'block', label: 'Block', glyph: <CursorGlyph shape="block" /> },
                          { value: 'bar', label: 'Bar', glyph: <CursorGlyph shape="bar" /> },
                          { value: 'underline', label: 'Underline', glyph: <CursorGlyph shape="underline" /> },
                        ]}
                      />
                    </Row>
                  )}
                </Section>
              )}
            </>
          )}
          {current.id === 'links' && (
            <Section id="links" title="Open links" card={false}>
              <LinksChoice />
            </Section>
          )}
          {current.id === 'voice' && <VoiceKeys />}
          {current.id === 'notify' && (
            <>
              <Section id="notify" title="When an agent needs you">
                <Row label="On this computer" sub="A banner when an agent needs you, even in the background.">
                  <Switch on={here} onChange={setNotifyHere} label="On this computer" />
                </Row>
                {s?.phone !== undefined && (
                  <Row label="On your phone" sub="A notification from the cmx app on iPhone.">
                    <Switch on={s.phone} onChange={(phone) => save({ phone })} label="On your phone" />
                  </Row>
                )}
                {s?.sound !== undefined && (
                  <Row label="Play a sound" sub="A short chime with the banner.">
                    <Switch on={s.sound} onChange={(sound) => save({ sound })} label="Play a sound" />
                  </Row>
                )}
              </Section>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
